import { useMemo } from 'react';
import { AlertTriangle, ArrowRight, CheckCircle2, Clock3, FileCheck2, Gavel, Layers3, ShieldCheck, Wallet } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useTodayData } from '@/hooks/useTodayData';
import { usePmoData } from '@/hooks/usePmoData';
import { useLocalization } from '@/hooks/useLocalization';
import { humanizeRule } from '@/components/risk/riskUtils';
import { healthTone, summarisePortfolio, waitingQueue, worstFirst, type HealthTone } from '@/lib/pmoSummary';
import { HomeActions, HomeEmpty, HomePanel, HomeRow, StatRow, StatTile } from '@/components/home/HomeFrame';

interface PMOCommandCenterProps {
  /** `params` are extra URL params, e.g. { project: id } to open that project's risks. */
  onNavigate: (view: string, params?: Record<string, string>) => void;
}

const TONE_CLASS: Record<HealthTone, string> = {
  'on-track': 'bg-success/10 text-foreground',
  'at-risk': 'bg-warning/15 text-foreground',
  critical: 'bg-destructive/10 text-destructive',
};
const TONE_LABEL: Record<HealthTone, string> = { 'on-track': 'On track', 'at-risk': 'At risk', critical: 'Critical' };
const SEVERITY_RANK: Record<string, number> = { critical: 3, high: 2, medium: 1, low: 0 };

/**
 * PMO home. The heading comes from the shell (roleHome), so this screen starts with its actions and numbers.
 * Every figure is the tenant's own data; anything not recorded says so instead of showing zero.
 */
const PMOCommandCenter = ({ onNavigate }: PMOCommandCenterProps) => {
  const { t, tx, currency, ago, shortDate } = useLocalization();
  const d = useTodayData();
  const pmo = usePmoData();

  const summary = useMemo(() => summarisePortfolio(pmo.projects), [pmo.projects]);
  const projects = useMemo(() => worstFirst(pmo.projects).slice(0, 8), [pmo.projects]);
  const queue = useMemo(() => waitingQueue(d.approvals, d.decisions).slice(0, 6), [d.approvals, d.decisions]);

  // Resolve a risk signal to the project it belongs to, so a click opens that project's risks.
  const { projectIdFor, nameFor } = useMemo(() => {
    const byProjectKey = new Map<string, { id: string; name: string }>();
    pmo.projects.forEach((p) => { byProjectKey.set(p.id, p); if (p.external_id) byProjectKey.set(p.external_id, p); });
    const taskByKey = new Map<string, { title: string; project: string | null }>();
    d.tasks.forEach((task) => { if (task.external_id) taskByKey.set(task.external_id, { title: task.title, project: task.project_external_id }); });
    return {
      projectIdFor: (entityType: string, entityId: string) =>
        entityType === 'project' ? byProjectKey.get(entityId)?.id : byProjectKey.get(taskByKey.get(entityId)?.project ?? '')?.id,
      nameFor: (entityType: string, entityId: string) =>
        entityType === 'project' ? byProjectKey.get(entityId)?.name : taskByKey.get(entityId)?.title,
    };
  }, [pmo.projects, d.tasks]);

  const exceptions = useMemo(() => [...d.risks]
    .sort((a, b) => (SEVERITY_RANK[b.severity] ?? 0) - (SEVERITY_RANK[a.severity] ?? 0))
    .slice(0, 6), [d.risks]);

  const waitingCount = d.approvals.length + d.decisions.length;
  const breachCount = pmo.breaches?.length ?? null;

  if (d.isLoading || pmo.isLoading) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label={t('Loading…')}>
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">{[0, 1, 2, 3].map((i) => <Skeleton key={i} className="h-28" />)}</div>
        <div className="grid gap-6 lg:grid-cols-3"><Skeleton className="h-72 lg:col-span-2" /><Skeleton className="h-72" /></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <HomeActions>
        <Button variant="outline" onClick={() => onNavigate('portfolio')}>{t('Open portfolio')}</Button>
        <Button onClick={() => onNavigate('decideBoard')}>{t('Open decision board')}</Button>
      </HomeActions>

      <StatRow>
        <StatTile
          label={t('Portfolio health')}
          icon={<Layers3 className="h-4 w-4" />}
          value={summary.total}
          tone={summary.critical > 0 ? 'critical' : summary.atRisk > 0 ? 'warning' : 'default'}
          hint={summary.total ? t('{0} at risk, {1} critical', { 0: summary.atRisk, 1: summary.critical }) : t('No projects synced yet.')}
          onClick={() => onNavigate('portfolio')}
        />
        <StatTile
          label={t('Budget')}
          icon={<Wallet className="h-4 w-4" />}
          value={summary.budgetUsedPct === null ? '—' : `${summary.budgetUsedPct}%`}
          tone={summary.overBudget > 0 ? 'critical' : 'default'}
          hint={summary.budget === null
            ? t('Not recorded')
            : `${t('{0} of {1} spent', { 0: currency(summary.spent ?? 0, { compact: true }), 1: currency(summary.budget, { compact: true }) })}${summary.overBudget ? ` · ${t('{0} over budget', { 0: summary.overBudget })}` : ''}`}
          onClick={() => onNavigate('portfolio')}
        />
        <StatTile
          label={t('Waiting on PMO')}
          icon={<Gavel className="h-4 w-4" />}
          value={waitingCount}
          tone={waitingCount > 0 ? 'warning' : 'default'}
          hint={t('{0} approvals · {1} decisions', { 0: d.approvals.length, 1: d.decisions.length })}
          onClick={() => onNavigate('decideBoard')}
        />
        <StatTile
          label={t('Policy breaches')}
          icon={<ShieldCheck className="h-4 w-4" />}
          value={breachCount === null ? '—' : breachCount}
          tone={breachCount ? 'critical' : 'default'}
          hint={breachCount === null ? t('Not recorded') : breachCount === 0 ? t('Everything is within policy') : t('AI outputs past their review deadline')}
          onClick={() => onNavigate('hitlWorkbench')}
        />
      </StatRow>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel
          className="lg:col-span-2"
          title={t('Portfolio health')}
          hint={t('Worst first. Open a project to see its risks.')}
          action={<Button variant="ghost" size="sm" onClick={() => onNavigate('portfolio')}>{t('View all')} <ArrowRight className="ms-1 h-4 w-4 rtl:rotate-180" /></Button>}
        >
          {projects.length === 0 ? (
            <HomeEmpty>{pmo.projectsFailed ? t('Projects could not be loaded.') : t('No projects synced yet.')}</HomeEmpty>
          ) : projects.map((project) => {
            const tone = healthTone(project.health);
            return (
              <HomeRow key={project.id} onClick={() => onNavigate('risks', { project: project.id })} label={t('{0}: {1}. Open its risks.', { 0: project.name, 1: t(TONE_LABEL[tone]) })}>
                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-2">
                    <span className="truncate text-sm font-semibold">{project.name}</span>
                    <span className={cn('shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium', TONE_CLASS[tone])}>{t(TONE_LABEL[tone])}</span>
                  </span>
                  <Progress value={project.progress ?? 0} className="mt-2 h-1.5" />
                </span>
                <span className="font-mono text-xs tabular-nums text-muted-foreground">{project.progress ?? 0}%</span>
              </HomeRow>
            );
          })}
        </HomePanel>

        <HomePanel title={t('Exceptions')} hint={t('Things that can change the plan.')} action={<Badge variant="outline">{exceptions.length}</Badge>}>
          {exceptions.length === 0 ? (
            <HomeEmpty>{t('No material exceptions.')}</HomeEmpty>
          ) : exceptions.map((risk) => {
            const projectId = projectIdFor(risk.entity_type, risk.entity_id);
            return (
              <HomeRow key={risk.id} onClick={() => onNavigate('risks', projectId ? { project: projectId } : undefined)}>
                <AlertTriangle className={cn('h-4 w-4 shrink-0', risk.severity === 'critical' || risk.severity === 'high' ? 'text-destructive' : 'text-warning')} aria-hidden="true" />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{nameFor(risk.entity_type, risk.entity_id) ?? tx(humanizeRule(risk.rule_id))}</span>
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">{tx(humanizeRule(risk.rule_id))} · {tx(risk.severity)} · {ago(risk.detected_at)}</span>
                </span>
              </HomeRow>
            );
          })}
          <Button variant="ghost" size="sm" className="w-full" onClick={() => onNavigate('risks')}>{t('Review risks')}</Button>
        </HomePanel>
      </div>

      <div className="grid gap-6 lg:grid-cols-3">
        <HomePanel
          className="lg:col-span-2"
          title={t('Waiting on PMO')}
          hint={t('Overdue decisions first, then the oldest items.')}
          action={<Badge variant="outline">{waitingCount}</Badge>}
        >
          {queue.length === 0 ? (
            <HomeEmpty>{t('Nothing is waiting on you.')}</HomeEmpty>
          ) : queue.map((item) => (
            <HomeRow key={`${item.kind}-${item.id}`} onClick={() => onNavigate(item.kind === 'approval' ? 'hitlWorkbench' : 'decisions')}>
              {item.kind === 'approval' ? <FileCheck2 className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" /> : <Gavel className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />}
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-medium">{item.title}</span>
                <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                  {item.kind === 'approval'
                    ? `${t('AI output awaiting review')} · ${ago(item.date)}`
                    : `${t('Decision pending')}${item.project ? ` · ${item.project}` : ''}${item.date ? ` · ${t('due {0}', { 0: shortDate(item.date) })}` : ''}`}
                </span>
              </span>
              {item.severity && <Badge variant="outline" className="shrink-0">{tx(item.severity)}</Badge>}
            </HomeRow>
          ))}
          {waitingCount > queue.length && (
            <Button variant="ghost" size="sm" className="w-full" onClick={() => onNavigate('decideBoard')}>{t('View all')} ({waitingCount})</Button>
          )}
        </HomePanel>

        <HomePanel title={t('Data freshness')} hint={t('A PMO dashboard is only useful when its evidence is current.')} action={<Clock3 className="h-4 w-4 text-muted-foreground" aria-hidden="true" />}>
          <div className="flex items-center justify-between text-sm">
            <span>{t('Projects not updated in 24h')}</span>
            <span className="font-semibold tabular-nums">{summary.stale}</span>
          </div>
          <div className="flex items-center gap-2 text-sm">
            {summary.stale === 0 ? <CheckCircle2 className="h-4 w-4 text-success" aria-hidden="true" /> : <AlertTriangle className="h-4 w-4 text-warning" aria-hidden="true" />}
            <span>{summary.stale === 0 ? t('Portfolio data is current') : t('Some portfolio data is stale')}</span>
          </div>
          <Button variant="outline" size="sm" onClick={() => onNavigate('deliverHub')}>{t('Inspect delivery data')}</Button>
        </HomePanel>
      </div>
    </div>
  );
};

export default PMOCommandCenter;
